import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../../../auth/authSlice";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  ListItemText,
  Chip,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useFirestore } from "react-redux-firebase";
import { configureCustomer } from "../../../../app/utils/models/configure/configureCustomer";
import { customerWrites } from "../../../../app/services/firestore/writes/customerWrites";
import notificationConfirm from "../../../../app/system-components/toasters/notificationConfirm";
import notificationError from "../../../../app/system-components/toasters/notificationError";
import {
  CUSTOMER_BILLING_ADDRESS,
  CUSTOMER_BILLING_CITY,
  CUSTOMER_BILLING_STATE,
  CUSTOMER_BILLING_ZIP,
  CUSTOMER_COMPANY_NAME,
  CUSTOMER_DISPLAY_NAME,
  CUSTOMER_SHIP_TO_BILLING,
  CUSTOMER_SHIPPING_ADDRESS,
  CUSTOMER_SHIPPING_CITY,
  CUSTOMER_SHIPPING_STATE,
  CUSTOMER_SHIPPING_ZIP,
  CUSTOMER_TAX_EXEMPT,
  CUSTOMER_TAX_RATE,
  CUSTOMER_TAX_RATE_DETAILS,
} from "../../../../app/utils/models/modelConstants/modelConstants";
import TextSubtitle from "../../../../app/system-components/typography/text/TextSubtitle";
import { WarningFilled } from "@ant-design/icons";
import { COLOR_GREEN_0, COLOR_RED_0 } from "../../../../app/ui/colorConstants";
import { useFirestoreFetch } from "../../../../app/services/hooks/fetch/useFirestoreFetch";
import { taxRateQueries } from "../../../../app/services/firestore/queries/taxRateQueries";
import { CategoryTag } from "../../../../app/system-components/tags/Tags";
import PaymentHistoryModal from "./PaymentHistoryModal";
import { useHistory } from "react-router-dom";
import {
  ROUTE_ADMIN_CUSTOMERS_PAYMENT_ROOT,
} from "../../../../app/routes";
import DisplayCustomerBalance from "../../../sharedComponents/DisplayCustomerBalance";
import CustomerInvoiceHistory from "./CustomerInvoiceHistory";
import AdditionalContacts from "./AdditionalContacts";
import { EditCustomerQboSync } from "./EditCustomerQboSync";
import Iconify from "../../../../app/iconify";
import { useCopyToClipboard } from "../../../../app/hooks/use-copy-to-clipboard";
import { enqueueSnackbar } from "notistack";
import { Close as CloseIcon, Warning as WarningIcon, ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { CustomerFiles } from "./CustomerFiles";

const EditCustomerModal = ({
  onCancel,
  onComplete,
  onContactAdded,
  visible,
  selected,
  hideDeactivate,
  showPaymentOptions,
}) => {

  const { copy } = useCopyToClipboard();

  const handleCopy = useCallback((text) => {
    enqueueSnackbar('Customer ID Copied');
    copy(text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copy, enqueueSnackbar]);

  const [selectedCustomer, setSelectedCustomer] = useState(selected)
  useEffect(() => {
    setSelectedCustomer(selected)
  }, [selected])
  

  const firestore = useFirestore();
  const history = useHistory();

  const { orgData, userData, fsOrgPrefix } = useSelector(authSelector);
  const { fetching, data: taxRates } = useFirestoreFetch(
    taxRateQueries().fetchActiveTaxRates
  );

  const [tabValue, setTabValue] = useState(0);  // Use numeric index instead of string
  const [billingShippingSame, setBillingShippingSame] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultDisplayEnabled, setDefaultDisplayEnabled] = useState(false);

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    defaultValues: {
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      customerCompanyName: '',
      customerDisplayName: '',
      email: '',
      phone: '',
      mobile: '',
      otherContact: '',
      customerBillingAddress: '',
      customerBillingCity: '',
      customerBillingState: '',
      customerBillingZip: '',
      customerShippingAddress: '',
      customerShippingCity: '',
      customerShippingState: '',
      customerShippingZip: '',
      shipToBilling: false,
      defaultDiscountPercent: 0,
      isTaxExempt: false,
      customerTaxRate: '',
      notes: '',
      checkNotesReminder: false,
      taxId: '',
      damageWaiverExempt: false,
      damageWaiverExemptReason: '',
    }
  });

  // Add tags fetching
  const { data: tags = [] } = useFirestoreFetch(
    () => firestore.collection(`orgs/${orgData?.id}/tags`).get()
      .then(snapshot => snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })))
  );

  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    if (selectedCustomer?.tagsQueryHook) {
      setSelectedTags(selectedCustomer.tagsQueryHook);
    }
  }, [selectedCustomer]);

  const handleTagChange = (event) => {
    const { value } = event.target;
    setSelectedTags(value);
  };

  const handleFinish = handleSubmit((values) => {
    setSaving(true);
    const selectedTr = taxRates.find(
      (r) => r.id === values[CUSTOMER_TAX_RATE]
    );
    const taxDetails = selectedTr ? selectedTr : null;
    const taxRate = selectedTr ? selectedTr.rate : null;

    const data = {
      ...selectedCustomer,
      title: values.title,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      suffix: values.suffix,
      customerCompanyName: values.customerCompanyName,
      customerDisplayName: values.customerDisplayName,
      email: values.email,
      phone: values.phone,
      mobile: values.mobile,
      otherContact: values.otherContact,
      customerBillingAddress: values.customerBillingAddress,
      customerBillingCity: values.customerBillingCity,
      customerBillingState: values.customerBillingState,
      customerBillingZip: values.customerBillingZip,
      customerShippingAddress: values.customerShippingAddress,
      customerShippingCity: values.customerShippingCity,
      customerShippingState: values.customerShippingState,
      customerShippingZip: values.customerShippingZip,
      shipToBilling: values.shipToBilling,
      defaultDiscountPercent: values.defaultDiscountPercent,
      isTaxExempt: values.isTaxExempt,
      taxDetails: taxDetails,
      customerTaxRate: taxRate,
      notes: values.notes,
      checkNotesReminder: values.checkNotesReminder,
      taxId: values.taxId,
      damageWaiverExempt: values.damageWaiverExempt,
      damageWaiverExemptReason: values.damageWaiverExemptReason,
      isActive: selectedCustomer?.isActive ?? true,
      updatedBy: userData.id,
      updatedOn: new Date(),
      tagsQueryHook: selectedTags,
    };

    console.log('Form values:', values);
    console.log('Data being saved:', data);

    const payload = configureCustomer().editCustomer(
      data,
      userData.id,
      orgData
    );

    console.log('Final payload:', payload);

    customerWrites({ firestore }, fsOrgPrefix)
      .editCustomer(selected.id, payload)
      .then(() => {
        clearBeforeClose();
        onComplete();
        enqueueSnackbar("Customer saved", { variant: "success" });
      })
      .catch((err) => {
        clearBeforeClose();
        console.error('Error saving customer:', err);
        enqueueSnackbar(
          "Something went wrong",
          { variant: "error" }
        );
      });
  });

  const clearBeforeClose = () => {
    setDefaultDisplayEnabled(false);
    setSaving(false);
    setBillingShippingSame(false);
    reset();
  };

  useEffect(() => {
    setSelectedCustomer(selected);
  }, [selected]);

  useEffect(() => {
    const loadCustomerData = async () => {
      if (!selectedCustomer || fetching) return;
      
      console.log('Loading customer data:', selectedCustomer); // Debug log
      
      const taxRate = selectedCustomer[CUSTOMER_TAX_RATE_DETAILS]
        ? selectedCustomer[CUSTOMER_TAX_RATE_DETAILS].id
        : '';
      
      const formValues = {
        title: selectedCustomer.title || '',
        firstName: selectedCustomer.firstName || '',
        middleName: selectedCustomer.middleName || '',
        lastName: selectedCustomer.lastName || '',
        suffix: selectedCustomer.suffix || '',
        customerCompanyName: selectedCustomer.customerCompanyName || '',
        customerDisplayName: selectedCustomer.customerDisplayName || '',
        email: selectedCustomer.email || '',
        phone: selectedCustomer.phone || '',
        mobile: selectedCustomer.mobile || '',
        otherContact: selectedCustomer.otherContact || '',
        customerBillingAddress: selectedCustomer.customerBillingAddress || '',
        customerBillingCity: selectedCustomer.customerBillingCity || '',
        customerBillingState: selectedCustomer.customerBillingState || '',
        customerBillingZip: selectedCustomer.customerBillingZip || '',
        customerShippingAddress: selectedCustomer.customerShippingAddress || '',
        customerShippingCity: selectedCustomer.customerShippingCity || '',
        customerShippingState: selectedCustomer.customerShippingState || '',
        customerShippingZip: selectedCustomer.customerShippingZip || '',
        shipToBilling: selectedCustomer.shipToBilling || false,
        defaultDiscountPercent: selectedCustomer.defaultDiscountPercent || 0,
        isTaxExempt: selectedCustomer.isTaxExempt || false,
        customerTaxRate: taxRate,
        notes: selectedCustomer.notes || '',
        checkNotesReminder: selectedCustomer.checkNotesReminder || false,
        taxId: selectedCustomer.taxId || '', // Ensure we're loading taxId
        damageWaiverExempt: selectedCustomer.damageWaiverExempt || false,
        damageWaiverExemptReason: selectedCustomer.damageWaiverExemptReason || '',
      };

      console.log('Setting form values:', formValues); // Debug log
      
      reset(formValues);
      
      setIsTaxExempt(selectedCustomer.isTaxExempt || false);
      setBillingShippingSame(selectedCustomer.shipToBilling || false);
    };

    loadCustomerData();
  }, [selectedCustomer, fetching, reset]);

  useEffect(() => {
    const validateForm = async () => {
      if (fetching || !visible || !selectedCustomer) return;
      const taxRate =
        selectedCustomer && selectedCustomer[CUSTOMER_TAX_RATE_DETAILS]
          ? selectedCustomer[CUSTOMER_TAX_RATE_DETAILS].id
          : null;

      const { customerTaxRate, ...rest } = selectedCustomer;
      setValue(CUSTOMER_TAX_RATE, taxRate);
      setValue(CUSTOMER_TAX_EXEMPT, selectedCustomer.isTaxExempt);

      if (selectedCustomer.isTaxExempt !== undefined) {
        setIsTaxExempt(selectedCustomer.isTaxExempt);
      }
      try {
        await control.validateFields();
        console.log("All fields are valid");
      } catch (error) {
        console.error("Validation failed for one or more fields", error);
      }
      setBillingShippingSame(selectedCustomer && selectedCustomer[CUSTOMER_SHIP_TO_BILLING]);
    };

    validateForm();
    
  }, [visible, fetching, selectedCustomer]);

  useEffect(() => {
    const values = getValues();
    setValue(CUSTOMER_SHIPPING_ADDRESS, values[CUSTOMER_BILLING_ADDRESS]);
    setValue(CUSTOMER_SHIPPING_CITY, values[CUSTOMER_BILLING_CITY]);
    setValue(CUSTOMER_SHIPPING_STATE, values[CUSTOMER_BILLING_STATE]);
    setValue(CUSTOMER_SHIPPING_ZIP, values[CUSTOMER_BILLING_ZIP]);
  }, [billingShippingSame]);

  const duplicateBilling = (formSuffix, target) => {
    if (!billingShippingSame) return;
    setValue(`customerShipping${formSuffix}`, target.value);
  };

  const handleDeactivate = () => {
    Dialog.confirm({
      title: "Make Inactive?",
      icon: <WarningFilled style={{ color: COLOR_RED_0 }} />,
      content:
        "If you are integrated with QuickBooks, this customer will also be made inactive in your QuickBooks account.",
      maskClosable: true,
      okType: "danger",
      cancelButtonProps: { disabled: saving },
      onOk() {
        return new Promise((resolve, reject) => {
          setSaving(true);
          customerWrites({ firestore }, fsOrgPrefix)
            .editCustomer(selectedCustomer && selectedCustomer.id, { isActive: false })
            .then(() => {
              resolve();
              clearBeforeClose();
              onComplete();
              notificationConfirm("Customer made inactive", "");
            })
            .catch((err) => {
              console.log(err);
              notificationError(
                "Something went wrong",
                "Customer could not be made inactive"
              );
            });
        });
      },
      onCancel() { },
    });
  };

  // MARK: Reactivate
  const handleReactivate = () => {
    Dialog.confirm({
      title: "Make Active?",
      icon: <WarningFilled style={{ color: COLOR_RED_0 }} />,
      content:
        "If you are integrated with QuickBooks, this customer will also be made active in your QuickBooks account.",
      maskClosable: true,
      okType: "danger",
      cancelButtonProps: { disabled: saving },
      onOk() {
        return new Promise((resolve, reject) => {
          setSaving(true);
          customerWrites({ firestore }, fsOrgPrefix)
            .editCustomer(selectedCustomer && selectedCustomer.id, { isActive: true })
            .then(() => {
              resolve();
              clearBeforeClose();
              onComplete();
              notificationConfirm("Customer made active", "");
            })
            .catch((err) => {
              console.log(err);
              notificationError(
                "Something went wrong",
                "Customer could not be made active"
              );
            });
        });
      },
      onCancel() { },
    });
  };

  const [isTaxExempt, setIsTaxExempt] = useState(false);

  const handleValuesChanged = (changedValues, allValues) => {
    const defaultDisplayName = generateDefaultDisplayName(allValues);
    const currDisplay = allValues[CUSTOMER_DISPLAY_NAME];
    setDefaultDisplayEnabled(currDisplay !== defaultDisplayName && defaultDisplayName !== "");

    if (changedValues[CUSTOMER_TAX_EXEMPT] !== undefined) {
      setIsTaxExempt(changedValues[CUSTOMER_TAX_EXEMPT]);
    }
  };

  // MARK: Display Name
  const generateDisplayName = () => {
    const values = getValues();
    const defaultDisplayName = generateDefaultDisplayName(values);
    setValue(CUSTOMER_DISPLAY_NAME, defaultDisplayName);
    setDefaultDisplayEnabled(false);
  };

  const renderOption = (t) => {
    return `${t.name} (${(t.rate * 100).toFixed(2)}%)`;
  };

  const configureDeactivateBtns = (selected) => {
    if (!selected) return null;
    if (hideDeactivate) return null;
    return (
      <Button
        style={{ float: "left" }}
        type={selected.isActive ? "danger" : ""}
        key="inactive"
        onClick={selected.isActive ? handleDeactivate : handleReactivate}
        disabled={saving}
      >
        {selected.isActive ? "Deactivate" : "Reactivate"}
      </Button>
    );
  };

  const configureDisabled = (selected) => {
    if (!selected) return false;
    return !selected.isActive;
  };

  const handleReceivePayment = () => {
    selectedCustomer.id &&
      history.push(`${ROUTE_ADMIN_CUSTOMERS_PAYMENT_ROOT}${selectedCustomer.id}`);
  };

  useEffect(() => {
    // Set the initial state for isTaxExempt based on initialValues
    setIsTaxExempt(getValues(CUSTOMER_TAX_EXEMPT));
  }, [getValues]);

  return (
    <Dialog
      open={visible}
      onClose={() => {
        onCancel();
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: '80%',
          maxWidth: '1200px',
          minHeight: '80vh'
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => handleCopy(selectedCustomer.id)}>
              <Iconify icon={"tabler:id"} />
            </IconButton>
            <Typography variant="h6">Edit Customer</Typography>
            {selectedCustomer && !selectedCustomer.isActive && (
              <CategoryTag text={"Deactivated"} />
            )}
          </Stack>
          
          {showPaymentOptions && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">
                <DisplayCustomerBalance customer={selectedCustomer} />
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  bgcolor: COLOR_GREEN_0,
                  '&:hover': { bgcolor: COLOR_GREEN_0 },
                }}
                onClick={handleReceivePayment}
              >
                Receive Payment
              </Button>
            </Stack>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="Customer" />
          <Tab label="Invoices" />
          <Tab label="Payments" />
          <Tab label="Additional Contacts" />
          <Tab label="Files" />

          {orgData && orgData.qboRealmId && orgData.qboRealmId !== "" && (
            <Tab label="QuickBooks Sync" />
          )}
        </Tabs>

        {tabValue === 0 && (
          <div>
            <CustomerInfoForm
              control={control}
              disabled={configureDisabled(selectedCustomer)}
              defaultDisplayEnabled={defaultDisplayEnabled}
              generateDisplayName={generateDisplayName}
              handleValuesChanged={handleValuesChanged}
              getValues={getValues}
              setDefaultDisplayEnabled={setDefaultDisplayEnabled}
            />
            <AddressSection
              control={control}
              disabled={configureDisabled(selectedCustomer)}
              billingShippingSame={billingShippingSame}
              setBillingShippingSame={setBillingShippingSame}
              duplicateBilling={duplicateBilling}
            />
            <InvoiceSettings
              control={control}
              disabled={configureDisabled(selectedCustomer)}
              isTaxExempt={isTaxExempt}
              setIsTaxExempt={setIsTaxExempt}
              taxRates={taxRates}
              fetching={fetching}
              tags={tags}
              selectedTags={selectedTags}
              handleTagChange={handleTagChange}
            />
          </div>
        )}

        {tabValue === 1 && (
          <CustomerInvoiceHistory
            customerId={selectedCustomer && selectedCustomer.id}
          />
        )}

        {tabValue === 2 && (
          <div>
            <PaymentHistoryModal
              customer={selectedCustomer && selectedCustomer}
            />
          </div>
        )}

        {tabValue === 3 && (
          <div>
            <AdditionalContacts
              customer={selectedCustomer && selectedCustomer}
              updateCustomer={setSelectedCustomer}
            />
          </div>
        )}

        {tabValue === 4 && (
          <div>
            <CustomerFiles
              customer={selectedCustomer && selectedCustomer}
            />
          </div>
        )}

        {tabValue === 5 && orgData && orgData.qboRealmId && (
          <div>
            <EditCustomerQboSync
              customer={selectedCustomer && selectedCustomer}
              orgId={orgData.id}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {tabValue === 0 ? (
          [
            selectedCustomer && configureDeactivateBtns(selectedCustomer),
            <Button
              key="cancel"
              onClick={() => {
                reset();
                onCancel();
              }}
              disabled={saving}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() => handleFinish()}
              disabled={saving || configureDisabled(selectedCustomer)}
              loading={saving}
            >
              Save
            </Button>,
          ]
        ) : (
          <Button
            key="close"
            onClick={() => {
              if (onContactAdded === null) {
                onCancel();
              }
              else {
                onContactAdded();
              }
            }}
            disabled={saving}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

function CustomerInfoForm({ 
  control, 
  disabled, 
  defaultDisplayEnabled,
  generateDisplayName,
  handleValuesChanged,
  getValues,
  setDefaultDisplayEnabled
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        CUSTOMER INFO
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={2}>
              <Controller
                name="title"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="firstName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={2}>
              <Controller
                name="middleName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Middle"
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="lastName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={2}>
              <Controller
                name="suffix"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Suffix"
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>

          <Controller
            name="customerCompanyName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Company Name"
                disabled={disabled}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
            )}
          />

          <Box sx={{ mb: 2 }}>
            <Controller
              name="customerDisplayName"
              control={control}
              defaultValue=""
              rules={{
                required: "Display name is required",
                pattern: {
                  value: /^[^:]*$/,
                  message: "Display name cannot contain ':'",
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Display Name"
                    disabled={disabled}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    sx={{ mb: 1 }}
                    onChange={(e) => {
                      field.onChange(e);
                      const currValue = e.target.value;
                      const values = getValues();
                      const defaultName = generateDefaultDisplayName(values);
                      setDefaultDisplayEnabled(currValue !== defaultName && defaultName !== "");
                    }}
                  />

                    <Button
                      size="small"
                      variant="contained"
                      onClick={generateDisplayName}
                      sx={{ mt: 1, mb: 1 }}
                    >
                      Generate Display Name
                    </Button>

                </Box>
              )}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Email"
                disabled={disabled}
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 2 }}
              />
            )}
          />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    disabled={disabled}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="mobile"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mobile"
                    disabled={disabled}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Controller
            name="otherContact"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Other Contact"
                disabled={disabled}
                fullWidth
                size="small"
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function AddressSection({
  control,
  disabled,
  billingShippingSame,
  setBillingShippingSame,
  duplicateBilling
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            BILLING ADDRESS
          </Typography>
          
          <Controller
            name="customerBillingAddress"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Street"
                disabled={disabled}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                onChange={(e) => {
                  field.onChange(e);
                  duplicateBilling("Address", e.target);
                }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Controller
                name="customerBillingCity"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    disabled={disabled}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      field.onChange(e);
                      duplicateBilling("City", e.target);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="customerBillingState"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="State"
                    disabled={disabled}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      field.onChange(e);
                      duplicateBilling("State", e.target);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="customerBillingZip"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="ZIP"
                    disabled={disabled}
                    fullWidth
                    size="small"
                    onChange={(e) => {
                      field.onChange(e);
                      duplicateBilling("Zip", e.target);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">
              SHIPPING ADDRESS
            </Typography>
            <Controller
              name="shipToBilling"
              control={control}
              defaultValue={false}
              render={({ field: { value, ...field } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={value}
                      disabled={disabled}
                      onChange={(e) => {
                        field.onChange(e);
                        setBillingShippingSame(e.target.checked);
                      }}
                    />
                  }
                  label="Use Billing"
                />
              )}
            />
          </Box>

          <Controller
            name="customerShippingAddress"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Street"
                disabled={disabled || billingShippingSame}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Controller
                name="customerShippingCity"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    disabled={disabled || billingShippingSame}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="customerShippingState"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="State"
                    disabled={disabled || billingShippingSame}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="customerShippingZip"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="ZIP"
                    disabled={disabled || billingShippingSame}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function InvoiceSettings({
  control,
  disabled,
  isTaxExempt,
  setIsTaxExempt,
  taxRates,
  fetching,
  tags,
  selectedTags,
  handleTagChange
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            INVOICE SETTINGS
          </Typography>

          <Controller
            name="defaultDiscountPercent"
            control={control}
            defaultValue={0}
            render={({ field }) => (
              <TextField
                {...field}
                label="Default Discount Percent"
                disabled={disabled}
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                }}
                size="small"
                sx={{ mb: 2, width: 200 }}
              />
            )}
          />

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Controller
              name="isTaxExempt"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange, ...field } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={value}
                      disabled={disabled}
                      onChange={(e) => {
                        onChange(e);
                        setIsTaxExempt(e.target.checked);
                      }}
                    />
                  }
                  label="Tax exempt"
                />
              )}
            />

            <Controller
              name="damageWaiverExempt"
              control={control}
              defaultValue={false}
              render={({ field: { value, ...field } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={value}
                      disabled={disabled}
                    />
                  }
                  label="Damage Waiver Exempt"
                />
              )}
            />
          </Stack>

          <Controller
            name="taxId"
            control={control}
            defaultValue=""
            
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Tax ID"
                placeholder="EIN/Tax ID Number"
                disabled={disabled}
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 2 }}
                // Normalize the input by removing any extra spaces
                onChange={(e) => {
                  const value = e.target.value.trim();
                  field.onChange(value);
                }}
              />
            )}
          />

          <Controller
            name="customerTaxRate"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Tax Rate</InputLabel>
                <Select
                  {...field}
                  disabled={isTaxExempt || fetching || disabled}
                  label="Tax Rate"
                >
                  {taxRates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {`${t.name} (${(t.rate * 100).toFixed(2)}%)`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            ADDITIONAL NOTES
          </Typography>

          <Controller
            name="notes"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                disabled={disabled}
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
          />

          <Controller
            name="checkNotesReminder"
            control={control}
            defaultValue={false}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={value}
                    disabled={disabled}
                  />
                }
                label="Remind me to review these notes when added to an order."
              />
            )}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="tags-label">Tags</InputLabel>
            <Select
              labelId="tags-label"
              multiple
              value={selectedTags}
              onChange={handleTagChange}
              disabled={disabled}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((tagId) => {
                    const tag = tags.find(t => t.id === tagId);
                    return tag ? (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        sx={{
                          backgroundColor: tag.color,
                          color: '#fff'
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {tags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  <Checkbox checked={selectedTags.indexOf(tag.id) > -1} />
                  <Box
                    component="span"
                    sx={{
                      width: 14,
                      height: 14,
                      mr: 1,
                      display: 'inline-block',
                      backgroundColor: tag.color
                    }}
                  />
                  <ListItemText primary={tag.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

function generateDefaultDisplayName(values) {
  const firstName = values.firstName || '';
  const lastName = values.lastName ? ` ${values.lastName}` : '';
  const co = values[CUSTOMER_COMPANY_NAME] 
    ? `${firstName}${lastName}`.trim() !== ''
      ? ` - ${values[CUSTOMER_COMPANY_NAME]}`
      : values[CUSTOMER_COMPANY_NAME]
    : '';
  return `${firstName}${lastName}${co}`.trim();
}

export default EditCustomerModal;
